import { Bill } from '@/types/bill';

interface AiReviewData {
  bill_number: string;
  session_code: string;
  version: string;
  action: string;
  analysis: {
    report_umd: {
      impact_category: string;
      rationale: string;
      impacts: Array<{
        description: string;
        rating: string;
        sentiment: string;
        bill_text: string;
        citation: string;
      }>;
    };
    report_higher_ed: {
      impacts: Array<{
        description: string;
        rating: string;
        sentiment: string;
        bill_text: string;
        citation: string;
      }>;
      summary: string;
    };
  };
}

/**
 * Generates the HTML content for an AI Review based on bill and review data
 * @param {Bill} bill - The bill object containing information like billNumber and title
 * @param {AiReviewData} reviewData - The AI review data containing analysis, impacts, etc.
 * @returns {string} - The HTML string representation of the AI Review
 */
export function generateAIReviewHTML(bill: Bill | null, reviewData: AiReviewData | null): string {
  if (!bill || !reviewData) {
    return '<div class="text-center py-12 text-muted-foreground">No AI review data available for this bill.</div>';
  }

  // Generate impact items HTML
  function generateImpactItems(impacts: Array<{
    description: string;
    rating: string;
    sentiment: string;
    bill_text: string;
    citation: string;
  }> | undefined): string {
    if (!impacts || !impacts.length) return '';
    
    return impacts.map(impact => {
      const sentimentStyles = {
        positive: "bg-green-100 text-green-800",
        negative: "bg-red-100 text-red-800",
        neutral: "bg-gray-100 text-gray-800"
      };
      
      // Icons are now hidden during print via CSS and replaced with color coding
      const sentimentIcons = {
        positive: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 thumb-up-icon"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>',
        negative: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 thumb-down-icon"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg>',
        neutral: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 scale-icon"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.375 1.375L3 12l5.813 1.912a2 2 0 0 1 1.375 1.375L12 21l1.912-5.813a2 2 0 0 1 1.375-1.375L21 12l-5.813-1.912a2 2 0 0 1-1.375-1.375L12 3Z"/></svg>'
      };
      
      const sentimentStyleClass = sentimentStyles[impact.sentiment as keyof typeof sentimentStyles] || sentimentStyles.neutral;
      const sentimentIcon = sentimentIcons[impact.sentiment as keyof typeof sentimentIcons] || sentimentIcons.neutral;

      return `
        <div class="border rounded-md p-3 mb-3">
          <div class="flex justify-between items-start mb-2">
            <div class="font-medium">${impact.description || ''}</div>
            <div class="badge variant-secondary ${sentimentStyleClass}">
              ${sentimentIcon}
              Rating: ${impact.rating || 'N/A'}
            </div>
          </div>
          
          ${impact.bill_text ? `
          <div class="mt-2 text-sm">
            <div class="text-muted-foreground mb-1 font-semibold">Referenced Bill Text:</div>
            <div class="bg-gray-50 p-2 rounded italic text-sm">${impact.bill_text}</div>
          </div>
          ` : ''}
          
          ${impact.citation ? `
          <div class="mt-2 text-xs text-muted-foreground">
            Citation: ${impact.citation}
          </div>
          ` : ''}
        </div>
      `;
    }).join('');
  }

  const billTitle = reviewData.action || bill.title || 'Legislative Review';
  const umdImpacts = generateImpactItems(reviewData.analysis?.report_umd?.impacts);
  const higherEdImpacts = generateImpactItems(reviewData.analysis?.report_higher_ed?.impacts);

  return `
<div class="print-container">
  <div class="AiReviewDialog__content max-w-3xl max-h-[90vh] overflow-y-auto p-3">
    <!-- Print header and footer (only visible when printing) -->
    <div class="print-header">
      ${bill.billNumber}: ${billTitle}
    </div>
    
    <div class="print-footer">
      Page <span class="page-number"></span> of <span class="page-count"></span>
    </div>
    
    <!-- Dialog Header -->
    <div class="flex justify-between items-center pb-1 mb-0">
      <div class="flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5">
          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <line x1="10" y1="9" x2="8" y2="9"/>
        </svg>
        AI Legislative Review: ${bill.billNumber}
      </div>
      <button class="flex items-center gap-2 print-visible ml-auto" onclick="window.print()">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4">
          <polyline points="6 9 6 2 18 2 18 9"/>
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
          <rect x="6" y="14" width="12" height="8"/>
        </svg>
        Print
      </button>
    </div>

    <div class="grid gap-0 py-1">
      <!-- UMD Impact Card -->
      <div class="card umd-impact-card" style="margin-bottom: -1.5rem">
        <div class="pb-1 p-2">
          <div class="text-xl">UMD Impact Analysis</div>
        </div>
        <div class="space-y-2 p-2 pt-0">
          <div class="flex items-center gap-2 mb-2">
            <div class="badge variant-outline text-sm">
              ${reviewData.analysis?.report_umd?.impact_category || 'N/A'}
            </div>
          </div>

          <div class="text-sm mb-4">
            <h4 class="font-semibold mb-1">Rationale:</h4>
            <p class="text-muted-foreground">${reviewData.analysis?.report_umd?.rationale || 'No rationale provided'}</p>
          </div>

          <hr class="separator" />

          <div>
            <h4 class="font-semibold mb-3">Detailed Impacts:</h4>
            ${umdImpacts || '<div class="text-muted-foreground">No impacts listed</div>'}
          </div>
        </div>
      </div>

      <!-- Higher Education Impact Card -->
      <div class="card higher-ed-impact-card" style="margin-top: -1.5rem">
        <div class="pb-1 p-2">
          <div class="text-xl">Higher Education Impact</div>
        </div>
        <div class="space-y-2 p-2 pt-0">
          <div class="text-sm mb-4">
            <h4 class="font-semibold mb-1">Summary:</h4>
            <p class="text-muted-foreground">${reviewData.analysis?.report_higher_ed?.summary || 'No summary provided'}</p>
          </div>

          <hr class="separator" />

          <div>
            <h4 class="font-semibold mb-3">Detailed Impacts:</h4>
            ${higherEdImpacts || '<div class="text-muted-foreground">No impacts listed</div>'}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  `;
}

/**
 * Exports the AI Review to a downloadable HTML file
 * @param {Bill} bill - The bill object
 * @param {AiReviewData} reviewData - The AI review data
 */
/**
 * Generates a complete HTML document for printing with enhanced page break styles
 * @param {Bill} bill - The bill object
 * @param {AiReviewData} reviewData - The AI review data
 * @returns {string} - The HTML document as a string
 */
export function generatePrintableHtml(bill: Bill | null, reviewData: AiReviewData | null): string {
  if (!bill || !reviewData) {
    return '';
  }
  
  // Generate impact items HTML with dedicated page break styling
  function generateImpactItems(impacts: Array<{
    description: string;
    rating: string;
    sentiment: string;
    bill_text: string;
    citation: string;
  }> | undefined): string {
    if (!impacts || !impacts.length) return '<div class="text-muted-foreground">No impacts listed</div>';
    
    return impacts.map(impact => {
      let sentimentClass = 'neutral';
      let sentimentIcon = '';
      
      switch(impact.sentiment) {
        case 'positive':
          sentimentClass = 'positive';
          sentimentIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 thumb-up-icon"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>';
          break;
        case 'negative':
          sentimentClass = 'negative';
          sentimentIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 thumb-down-icon"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg>';
          break;
        default:
          sentimentIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 scale-icon"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.375 1.375L3 12l5.813 1.912a2 2 0 0 1 1.375 1.375L12 21l1.912-5.813a2 2 0 0 1 1.375-1.375L21 12l-5.813-1.912a2 2 0 0 1-1.375-1.375L12 3Z"/></svg>';
      }

      return `
        <div class="impact-item">
          <div class="impact-header">
            <div class="impact-description">${impact.description || ''}</div>
            <div class="impact-rating ${sentimentClass}">
              ${sentimentIcon}
              Rating: ${impact.rating || 'N/A'}
            </div>
          </div>
          
          ${impact.bill_text ? `
          <div class="bill-text-container">
            <div class="bill-text-label">Referenced Bill Text:</div>
            <div class="bill-text">${impact.bill_text}</div>
          </div>
          ` : ''}
          
          ${impact.citation ? `
          <div class="citation">
            Citation: ${impact.citation}
          </div>
          ` : ''}
        </div>
      `;
    }).join('');
  }

  const billNumber = bill.billNumber;
  const billTitle = reviewData.action || bill.title || 'Legislative Review';
  const umdImpacts = generateImpactItems(reviewData.analysis?.report_umd?.impacts);
  const higherEdImpacts = generateImpactItems(reviewData.analysis?.report_higher_ed?.impacts);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>AI Review - ${billNumber}</title>
  <style>
    /* Base styles */
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      line-height: 1.5;
      color: #333;
      margin: 0;
      padding: 2rem;
      width: 100%;
      max-width: 100%;
      overflow-x: hidden;
    }
    
    /* Print container to fix right border issues */
    .print-container {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0 15px;
      box-sizing: border-box;
    }
    
    /* Headers and content */
    .print-header {
      text-align: center;
      font-size: 14px;
      color: #666;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #eee;
    }
    .page-title {
      font-size: 22px;
      font-weight: bold;
      text-align: center;
      margin: 20px 0;
    }
    .content-section {
      padding: 20px;
    }
    .separator {
      height: 1px;
      background-color: #eee;
      margin: 20px 0;
    }
    h4 {
      font-size: 16px;
      font-weight: bold;
      margin: 15px 0 10px 0;
    }
    
    /* Impact items */
    .impact-item {
      border: 1px solid #eee;
      border-radius: 6px;
      padding: 15px;
      margin-bottom: 15px;
    }
    .impact-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    .impact-description {
      font-weight: 500;
    }
    .impact-rating {
      background-color: #f5f5f5;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 12px;
      display: inline-flex;
      align-items: center;
    }
    .bill-text-container {
      margin-top: 10px;
    }
    .bill-text-label {
      font-weight: 600;
      color: #666;
      margin-bottom: 5px;
      font-size: 14px;
    }
    .bill-text {
      background-color: #f9f9f9;
      padding: 10px;
      border-radius: 4px;
      font-style: italic;
      margin: 0 0 10px 0;
      font-size: 14px;
    }
    .citation {
      font-size: 12px;
      color: #666;
      margin-top: 5px;
    }
    
    /* Sentiment colors */
    .positive {
      background-color: #d1fae5;
      color: #065f46;
    }
    .negative {
      background-color: #fee2e2;
      color: #991b1b;
    }
    .neutral {
      background-color: #f3f4f6;
      color: #1f2937;
    }
    
    /* Badge for impact category */
    .badge {
      display: inline-block;
      padding: 4px 8px;
      background-color: #f5f5f5;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      font-size: 12px;
      margin-right: 8px;
    }
    
    /* Print-specific styles */
    @media print {
      /* Hide thumb icons during print */
      .thumb-up-icon, .thumb-down-icon, .scale-icon {
        display: none !important;
      }
      
      /* Style for right border fix */
      @page {
        size: A4;
        margin: 2cm 1.5cm 2cm 1.5cm;
      }

      html, body, .print-container {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: hidden !important;
      }
      
      /* Content container settings */
      .print-container {
        width: calc(100% - 30px) !important;
        max-width: calc(100% - 30px) !important;
        padding: 0 15px !important;
        box-sizing: border-box !important;
        margin: 0 auto !important;
      }
      
      /* Ensure all content fits within printable area with padding */
      .content-section, article, section, .impact-item, .bill-text, .impact-header {
        max-width: 100% !important;
        box-sizing: border-box !important;
        overflow-wrap: break-word !important;
      }
      
      /* Page break settings */
      .page1 {
        page-break-after: always;
        break-after: page;
      }
      
      .page2 {
        page-break-before: always;
        break-before: page;
      }
    }
  </style>
</head>
<body>
  <div class="print-container">
    <!-- First Page: UMD Impact Analysis -->
    <article class="page1">
    <header class="print-header">
      ${billNumber}: ${billTitle}
    </header>
    
    <section class="content-section">
      <h2 class="page-title">UMD Impact Analysis</h2>
      
      <div class="badge">
        ${reviewData.analysis?.report_umd?.impact_category || 'N/A'}
      </div>
      
      <div style="margin: 20px 0;">
        <h4>Rationale:</h4>
        <p>${reviewData.analysis?.report_umd?.rationale || 'No rationale provided'}</p>
      </div>
      
      <div class="separator"></div>
      
      <div>
        <h4>Detailed Impacts:</h4>
        ${umdImpacts}
      </div>
    </section>
  </article>
  
  <!-- Second Page: Higher Education Impact -->
  <article class="page2">
    <header class="print-header">
      ${billNumber}: ${billTitle}
    </header>
    
    <section class="content-section">
      <h2 class="page-title">Higher Education Impact</h2>
      
      <div style="margin: 20px 0;">
        <h4>Summary:</h4>
        <p>${reviewData.analysis?.report_higher_ed?.summary || 'No summary provided'}</p>
      </div>
      
      <div class="separator"></div>
      
      <div>
        <h4>Detailed Impacts:</h4>
        ${higherEdImpacts}
      </div>
    </section>
  </article>
  </div>
</body>
</html>
`;
}

/**
 * Creates an iframe with the printable content and triggers printing
 * @param {Bill} bill - The bill object
 * @param {AiReviewData} reviewData - The AI review data
 */
export function printAIReview(bill: Bill | null, reviewData: AiReviewData | null): void {
  if (!bill || !reviewData) {
    alert('No AI review data available to print');
    return;
  }
  
  // Generate printable HTML
  const printableHtml = generatePrintableHtml(bill, reviewData);
  
  // Create an invisible iframe
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.left = '-9999px'; // Move off-screen
  document.body.appendChild(iframe);
  
  // Write content to the iframe
  const iframeDocument = iframe.contentWindow?.document;
  if (iframeDocument) {
    iframeDocument.open();
    iframeDocument.write(printableHtml);
    iframeDocument.close();
    
    // Wait for content to load before printing
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  }
}

/**
 * Exports the AI Review to a downloadable HTML file
 * @param {Bill} bill - The bill object
 * @param {AiReviewData} reviewData - The AI review data
 */
export function exportAIReviewToHtml(bill: Bill | null, reviewData: AiReviewData | null): void {
  if (!bill || !reviewData) {
    alert('No AI review data available to export');
    return;
  }
  
  // Generate the printable HTML
  const htmlDocument = generatePrintableHtml(bill, reviewData);
  
  // Create a download link and trigger the download
  const blob = new Blob([htmlDocument], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${bill.billNumber}_AI_Review.html`;
  document.body.appendChild(a);
  a.click();
  
  // Clean up
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Generates a complete HTML document for bulk printing multiple bill reviews
 * Each bill starts on a new page and for each bill, higher education impact starts on a new page
 * @param {Bill[]} bills - Array of bill objects
 * @param {Record<string, AiReviewData | null>} reviewsData - Object mapping bill numbers to review data
 * @returns {string} - The HTML document as a string
 */
export function generateBulkPrintableHtml(bills: Bill[], reviewsData: Record<string, AiReviewData | null>): string {
  if (!bills.length || Object.keys(reviewsData).length === 0) {
    return '<div class="text-center py-12 text-muted-foreground">No AI review data available to print</div>';
  }
  
  // Generate the HTML content for each bill
  const billReviewsHtml = bills.map((bill, index) => {
    const reviewData = reviewsData[bill.billNumber];
    
    if (!reviewData) {
      return `
        <!-- Bill ${bill.billNumber} - No data -->
        <article class="bill-review">
          <header class="print-header">
            ${bill.billNumber}: ${bill.title || 'Legislative Review'}
          </header>
          
          <div class="content-section">
            <h2 class="bill-heading">${bill.billNumber}</h2>
            <h3 class="bill-title">${bill.title || 'No Title'}</h3>
            
            <div class="no-review-message">
              <p>No AI review data available for this bill.</p>
            </div>
          </div>
          
          <!-- Force page break between bills -->
          ${index < bills.length - 1 ? '<div class="bill-page-break"></div>' : ''}
        </article>
      `;
    }

    // For bills with data, use structure similar to single bill print
    // with UMD impact on first page and Higher Ed on second page
    return `
      <!-- Bill ${bill.billNumber} -->
      <article class="bill-review">
        <header class="print-header">
          ${bill.billNumber}: ${reviewData.action || bill.title || 'Legislative Review'}
        </header>
        
        <!-- UMD Impact Section -->
        <section class="content-section umd-section">
          <h2 class="bill-heading">${bill.billNumber}</h2>
          <h3 class="bill-title">${reviewData.action || bill.title || 'Legislative Review'}</h3>
          
          <h4 class="section-title">UMD Impact Analysis</h4>
          
          <div class="badge">
            ${reviewData.analysis?.report_umd?.impact_category || 'N/A'}
          </div>
          
          <div class="rationale-section">
            <h5 class="subsection-title">Rationale:</h5>
            <p>${reviewData.analysis?.report_umd?.rationale || 'No rationale provided'}</p>
          </div>
          
          <div class="separator"></div>
          
          <div class="impacts-section">
            <h5 class="subsection-title">Detailed Impacts:</h5>
            ${generateImpactItemsHtml(reviewData.analysis?.report_umd?.impacts)}
          </div>
        </section>

        <!-- Higher Education Section (will start on new page via CSS) -->
        <section class="content-section higher-ed-section">
          <header class="section-header">
            ${bill.billNumber}: ${reviewData.action || bill.title || 'Legislative Review'}
          </header>
          
          <h4 class="section-title">Higher Education Impact</h4>
          
          <div class="summary-section">
            <h5 class="subsection-title">Summary:</h5>
            <p>${reviewData.analysis?.report_higher_ed?.summary || 'No summary provided'}</p>
          </div>
          
          <div class="separator"></div>
          
          <div class="impacts-section">
            <h5 class="subsection-title">Detailed Impacts:</h5>
            ${generateImpactItemsHtml(reviewData.analysis?.report_higher_ed?.impacts)}
          </div>
        </section>
      </article>
      
      <!-- Force page break between bills -->
      ${index < bills.length - 1 ? '<div class="bill-page-break"></div>' : ''}
    `;
  }).join('');

  // Helper function to generate impact items HTML
  function generateImpactItemsHtml(impacts: Array<{
    description: string;
    rating: string;
    sentiment: string;
    bill_text: string;
    citation: string;
  }> | undefined): string {
    if (!impacts || !impacts.length) return '<div class="no-impacts">No impacts listed</div>';
    
    return impacts.map(impact => {
      let sentimentClass = 'neutral';
      let sentimentIcon = '';
      
      switch(impact.sentiment) {
        case 'positive':
          sentimentClass = 'positive';
          sentimentIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 thumb-up-icon"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg>';
          break;
        case 'negative':
          sentimentClass = 'negative';
          sentimentIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 thumb-down-icon"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg>';
          break;
        default:
          sentimentIcon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 mr-1 scale-icon"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.375 1.375L3 12l5.813 1.912a2 2 0 0 1 1.375 1.375L12 21l1.912-5.813a2 2 0 0 1 1.375-1.375L21 12l-5.813-1.912a2 2 0 0 1-1.375-1.375L12 3Z"/></svg>';
      }

      return `
        <div class="impact-item">
          <div class="impact-header">
            <div class="impact-description">${impact.description || ''}</div>
            <div class="impact-rating ${sentimentClass}">
              ${sentimentIcon}
              Rating: ${impact.rating || 'N/A'}
            </div>
          </div>
          
          ${impact.bill_text ? `
          <div class="bill-text-container">
            <div class="bill-text-label">Referenced Bill Text:</div>
            <div class="bill-text">${impact.bill_text}</div>
          </div>
          ` : ''}
          
          ${impact.citation ? `
          <div class="citation">
            Citation: ${impact.citation}
          </div>
          ` : ''}
        </div>
      `;
    }).join('');
  }

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Bulk AI Reviews</title>
  <style>
    /* Base styles */
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      line-height: 1.5;
      color: #333;
      margin: 0;
      padding: 2rem;
      width: 100%;
      max-width: 100%;
      overflow-x: hidden;
    }
    
    /* Print container to fix right border issues */
    .print-container {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0 15px;
      box-sizing: border-box;
    }
    
    /* Headers and content */
    .print-header {
      text-align: center;
      font-size: 14px;
      color: #666;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #eee;
    }
    
    .bill-heading {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 8px;
    }
    
    .bill-title {
      font-size: 18px;
      margin-bottom: 24px;
    }
    
    .section-title {
      font-size: 20px;
      font-weight: bold;
      margin: 20px 0;
    }
    
    .subsection-title {
      font-size: 16px;
      font-weight: bold;
      margin: 15px 0 10px 0;
    }
    
    .content-section {
      padding: 20px;
    }
    
    .separator {
      height: 1px;
      background-color: #eee;
      margin: 20px 0;
    }
    
    /* Impact items */
    .impact-item {
      border: 1px solid #eee;
      border-radius: 6px;
      padding: 15px;
      margin-bottom: 15px;
    }
    
    .impact-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    
    .impact-description {
      font-weight: 500;
    }
    
    .impact-rating {
      background-color: #f5f5f5;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 12px;
      display: inline-flex;
      align-items: center;
    }
    
    .bill-text-container {
      margin-top: 10px;
    }
    
    .bill-text-label {
      font-weight: 600;
      color: #666;
      margin-bottom: 5px;
      font-size: 14px;
    }
    
    .bill-text {
      background-color: #f9f9f9;
      padding: 10px;
      border-radius: 4px;
      font-style: italic;
      margin: 0 0 10px 0;
      font-size: 14px;
    }
    
    .no-review-message,
    .no-impacts {
      text-align: center;
      padding: 20px;
      color: #666;
      font-style: italic;
    }
    
    .citation {
      font-size: 12px;
      color: #666;
      margin-top: 5px;
    }
    
    /* Sentiment colors */
    .positive {
      background-color: #d1fae5;
      color: #065f46;
    }
    
    .negative {
      background-color: #fee2e2;
      color: #991b1b;
    }
    
    .neutral {
      background-color: #f3f4f6;
      color: #1f2937;
    }
    
    /* Badge for impact category */
    .badge {
      display: inline-block;
      padding: 4px 8px;
      background-color: #f5f5f5;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      font-size: 12px;
      margin-right: 8px;
    }
    
    /* Print-specific styles */
    @media print {
      /* Hide thumb icons during print */
      .thumb-up-icon, .thumb-down-icon, .scale-icon {
      display: none !important;
      }
      
      /* Style for right border fix */
      @page {
      size: A4;
      margin: 2cm 1.5cm 2cm 1.5cm;
      }

      html, body, .print-container {
        width: 100% !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow-x: hidden !important;
    }
      
      /* Content container settings */
      .print-container {
        width: calc(100% - 30px) !important;
        max-width: calc(100% - 30px) !important;
        padding: 0 15px !important;
        box-sizing: border-box !important;
        margin: 0 auto !important;
      }
      
      /* Ensure all content fits within printable area with padding */
      .content-section, article, section, .impact-item, .bill-text, .impact-header {
        max-width: 100% !important;
        box-sizing: border-box !important;
        overflow-wrap: break-word !important;
      }
      
      /* Force page breaks between bills and sections */
      .bill-page-break {
        page-break-after: always;
        break-after: page;
        height: 0;
        margin: 0;
        padding: 0;
      }
      
      /* Higher Ed section should start on a new page */
      .higher-ed-section {
        page-break-before: always;
        break-before: page;
      }
      
      /* Keep sections intact */
      .umd-section, .higher-ed-section {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      /* Avoid page breaks inside impact items */
      .impact-item {
        page-break-inside: avoid;
        break-inside: avoid;
      }
    }
    
    /* Safari-specific fixes */
    @media print and (-webkit-min-device-pixel-ratio:0) {
      .umd-section {
        padding-bottom: 5mm !important;
      }
      
      .higher-ed-section {
        padding-top: 5mm !important;
      }
      
      /* Additional fix to ensure no blank pages */
      .bill-review {
        break-inside: avoid;
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="print-container">
    ${billReviewsHtml}
  </div>
</body>
</html>
  `;
}

/**
 * Creates an iframe with the bulk printable content and triggers printing
 * @param {Bill[]} bills - Array of bill objects
 * @param {Record<string, AiReviewData | null>} reviewsData - Object mapping bill numbers to review data
 */
export function printBulkAIReviews(bills: Bill[], reviewsData: Record<string, AiReviewData | null>): void {
  if (!bills.length || Object.keys(reviewsData).length === 0) {
    alert('No AI review data available to print');
    return;
  }
  
  // Generate printable HTML
  const printableHtml = generateBulkPrintableHtml(bills, reviewsData);
  
  // Create an invisible iframe
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.left = '-9999px'; // Move off-screen
  document.body.appendChild(iframe);
  
  // Write content to the iframe
  const iframeDocument = iframe.contentWindow?.document;
  if (iframeDocument) {
    iframeDocument.open();
    iframeDocument.write(printableHtml);
    iframeDocument.close();
    
    // Wait for content to load before printing
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  }
}
